import { NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, FileText, Users, Wrench,
  ReceiptText, UserCheck, LogOut, CalendarDays,
} from 'lucide-react'
import { ROUTES } from '@/config/routes'
import useAuthStore from '@/stores/authStore'

const NAV = [
  { to: ROUTES.ADMIN,             icon: LayoutDashboard, label: 'Dashboard'   },
  { to: ROUTES.ADMIN_QUOTES,      icon: FileText,        label: 'Proposals'   },
  { to: ROUTES.ADMIN_SITE_VISITS, icon: CalendarDays,    label: 'Site Visits' },
  { to: ROUTES.ADMIN_WORKERS,     icon: UserCheck,       label: 'Workers'     },
  { to: ROUTES.ADMIN_CUSTOMERS,   icon: Users,           label: 'Customers'   },
  { to: ROUTES.ADMIN_FINANCIALS,  icon: ReceiptText,     label: 'Payments'    },
  { to: ROUTES.ADMIN_RATES,       icon: Wrench,          label: 'Rates'       },
]

export default function AdminLayout({ children }) {
  const navigate  = useNavigate()
  const signOut   = useAuthStore((s) => s.signOut)
  const profile   = useAuthStore((s) => s.profile)

  async function handleSignOut() {
    await signOut()
    navigate(ROUTES.LOGIN, { replace: true })
  }

  return (
    <div className="min-h-dvh bg-surface-base flex">

      {/* ── Sidebar (desktop) ───────────────────────────────────────────────── */}
      <aside className="hidden md:flex flex-col w-56 shrink-0 bg-surface-raised border-r border-surface-border">

        {/* Logo */}
        <div className="px-5 py-5 border-b border-surface-border">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-siglat-teal/15 rounded-lg flex items-center justify-center">
              <span className="text-siglat-teal text-xs font-black">SK</span>
            </div>
            <div>
              <p className="text-xs font-bold text-ink-primary leading-none">Siglat Konstract</p>
              <p className="text-[10px] text-ink-muted">Admin Panel</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 px-2 flex flex-col gap-0.5">
          {NAV.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === ROUTES.ADMIN}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors
                ${isActive
                  ? 'bg-siglat-teal/15 text-siglat-teal font-semibold'
                  : 'text-ink-secondary hover:bg-surface-overlay hover:text-ink-primary'}`
              }
            >
              <Icon className="w-4 h-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Profile + sign out */}
        <div className="px-3 py-4 border-t border-surface-border">
          <p className="text-xs text-ink-muted px-2 mb-2 truncate">{profile?.full_name}</p>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-2 px-3 py-2 w-full rounded-lg text-sm text-ink-secondary hover:bg-surface-overlay hover:text-ink-primary transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* ── Main content ────────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Mobile top bar */}
        <div className="md:hidden bg-surface-raised border-b border-surface-border px-4 py-3 flex items-center justify-between safe-top">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-siglat-teal/15 rounded-lg flex items-center justify-center">
              <span className="text-siglat-teal text-xs font-black">SK</span>
            </div>
            <p className="text-sm font-bold text-ink-primary">Admin Panel</p>
          </div>
          <button onClick={handleSignOut} className="text-ink-muted hover:text-ink-primary">
            <LogOut className="w-4 h-4" />
          </button>
        </div>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-surface-raised border-t border-surface-border flex safe-bottom">
          {NAV.slice(0, 5).map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === ROUTES.ADMIN}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center gap-0.5 py-2 text-[10px] transition-colors
                ${isActive ? 'text-siglat-teal' : 'text-ink-muted'}`
              }
            >
              <Icon className="w-5 h-5" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Page */}
        <main className="flex-1 overflow-y-auto pb-20 md:pb-0">
          {children}
        </main>

      </div>
    </div>
  )
}
